import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import type { ReactElement } from "react";

/**
 * Pill-row picker shared by structured onboarding forms — used for
 * single-select choices like jurisdiction (US / non-US) or capital
 * range. Options render as toggle buttons; `disabled` locks the row
 * and dims every unselected option while preserving emphasis on the
 * committed choice.
 */
export function SegmentedPicker<T extends string>({
    options,
    value,
    onChange,
    size = "md",
    disabled = false
}: {
    options: Array<{ value: T; label: string }>;
    value: T | null;
    onChange: (v: T) => void;
    size?: "sm" | "md";
    /** Locks the picker — clicks no-op, hover effects drop, unselected
     *  options dim. Selected value (if any) stays at full emphasis so
     *  the committed choice reads clearly. */
    disabled?: boolean;
}): ReactElement {
    return (
        <div className="flex flex-wrap gap-1.5">
            {options.map((opt) => {
                const selected = value === opt.value;
                return (
                    // The `toggle` variant's own `disabled:` palette carries
                    // the demotion — brand-ui §1 bans `opacity-*` dimming on
                    // interactive elements, and `aria-selected` keeps the
                    // committed choice at full emphasis regardless.
                    <Button
                        key={opt.value}
                        type="button"
                        variant="toggle"
                        interaction="default"
                        size={size === "sm" ? "sm" : "md"}
                        disabled={disabled}
                        aria-selected={selected ? "true" : undefined}
                        onClick={() => onChange(opt.value)}
                        className="rounded-xs"
                    >
                        <Text variant="small" render={<span />}>
                            {opt.label}
                        </Text>
                    </Button>
                );
            })}
        </div>
    );
}
